import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'hooks/redux'
import { setShowInvestmentInfo } from 'store/modals/actions'
import BackArrow from 'assets/svg/BackArrow'
import Drawer from 'react-modern-drawer'
import {
  Container,
  BackArrowButton,
  Title,
  Body,
  ContentContainer,
  Header,
  Section,
  SectionTitle,
  Text,
  LinkContainer,
  LinkItem,
  Button,
  getDrawerStyles,
  StrongUnderline
} from './style'
import DownloadSVG from 'assets/svg/DownloadSVG'
import useNavigation from 'hooks/useNavigation'
import { InvestmentServices } from 'services'
import { SpinnerIcon } from 'components/organisms/Table/styles'

export enum DocumentType {
  TERMS_SHEET = 'termsSheet',
  FACTS_SHEET = 'factsSheet'
}

const DrawerInvestmentInfo: React.FC = () => {
  const { showDrawerInvestmentInfo } = useAppSelector(state => state.modal)
  const { investmentHours, fund } = useAppSelector(state => state.investments)
  const dispatch = useDispatch()
  const { onNavigate } = useNavigation()
  const [loadingTerms, setLoadingTerms] = useState(false)
  const [loadingFactsheet, setLoadingFactsheet] = useState(false)

  const subscriptionHoursText = investmentHours?.subscription?.join(' a ') || ''
  const rescueHoursText = investmentHours?.rescue?.join(' a ') || ''

  const onClose = useCallback(() => {
    dispatch(setShowInvestmentInfo(false))
  }, [dispatch])

  const handleTermsDownload = async (documentType: DocumentType) => {
    try {
      let response
      let fileName

      if (documentType === DocumentType.TERMS_SHEET) {
        setLoadingTerms(true)
        response = await InvestmentServices.getTermsheet()
        fileName = 'reglamento-fondo.pdf'
      } else {
        setLoadingFactsheet(true)
        response = await InvestmentServices.getFactsheet()
        fileName = 'folleto-fondo.pdf'
      }
      const base64Data = response.data.data

      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error on download document:', error)
    } finally {
      setLoadingTerms(false)
      setLoadingFactsheet(false)
    }
  }

  return (
    <Drawer
      open={showDrawerInvestmentInfo}
      onClose={onClose}
      direction="right"
      style={getDrawerStyles()}
    >
      <Container>
        <Header>
          <BackArrowButton type="button" onClick={onClose}>
            <BackArrow />
          </BackArrowButton>
          <Title>Inversiones</Title>
        </Header>

        <Body>
          <ContentContainer>
            <Section heightPercent={55}>
              <SectionTitle>Objetivo</SectionTitle>
              <Text>
                El objetivo es la preservación del capital, a través de una
                estrategia diversificada, sin asumir riesgos significativos. Se
                prioriza la disponibilidad inmediata de sus inversiones y se
                busca minimizar la incidencia de las fluctuaciones del mercado.
              </Text>
            </Section>

            <Section heightPercent={55}>
              <SectionTitle>Características principales</SectionTitle>
              <Text>
                <StrongUnderline>Moneda de suscripción:</StrongUnderline>
                {' '}
                Peso Argentino
                <br />
                <StrongUnderline>Horario de suscripción:</StrongUnderline>
                {' '}
                {subscriptionHoursText ? `Lunes a Viernes de ${subscriptionHoursText} h` : '—'}
                <br />
                <StrongUnderline>Horario de rescate:</StrongUnderline>
                {' '}
                {rescueHoursText ? `Lunes a Viernes de ${rescueHoursText} h` : '—'}
                <br />
                <StrongUnderline>Plazo de liquidación:</StrongUnderline>
                {' '}
                La liquidación se realiza al final del día, si la orden ingresó antes del cierre
                <br />
                <StrongUnderline>Perfil de riesgo:</StrongUnderline>
                {' '}
                Conservador
                <br />
                <StrongUnderline>Monto mínimo:</StrongUnderline>
                {' '}
                {`$${Number(fund?.minimumAmount ?? 1).toLocaleString('es-AR', {
                  useGrouping: false,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}`}
              </Text>
            </Section>

            <LinkContainer>
              <LinkItem
                onClick={() => handleTermsDownload(DocumentType.FACTS_SHEET)}
              >
                Folleto
                {' '}
                {loadingFactsheet ? <SpinnerIcon /> : <DownloadSVG />}
              </LinkItem>
              <LinkItem
                onClick={() => handleTermsDownload(DocumentType.TERMS_SHEET)}
              >
                Reglamento
                {' '}
                {loadingTerms ? <SpinnerIcon /> : <DownloadSVG />}
              </LinkItem>
            </LinkContainer>

            <Button
              onClick={() => {
                onClose()
                onNavigate('/content/customer-support')
              }}
              type="button"
            >
              Necesito ayuda
            </Button>
          </ContentContainer>
        </Body>
      </Container>
    </Drawer>
  )
}

export default DrawerInvestmentInfo
